import { FC, useContext, useEffect, useMemo } from 'react';
import { UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';
import { QUERY_teamMemberPageSeniorityMethod } from 'queries';
import {
  SeniorityMethodEnum,
  TeamMemberPageSenorityMethodQuery,
  TeamMemberPageSenorityMethodQueryVariables,
} from 'types/graphql';
import { GQLAPIContextType } from 'types/common.types';
import { createGQLDataContext } from 'utils/createDataContext';
import { useGQLQuery } from 'hooks';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { useSelector } from 'react-redux';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { createApolloCache, createApolloClient } from 'providers/ApolloProvider';

const client = createApolloClient({ cache: createApolloCache() });

export const communitySeniorityMethodConfig = (
  locationId: number,
): UseGQLQueryConfig<
  TeamMemberPageSenorityMethodQuery,
  TeamMemberPageSenorityMethodQuery,
  TeamMemberPageSenorityMethodQueryVariables
> => {
  return {
    key: 'FETCH_TEAM_MEMBER_PAGE_SENIORITY_METHOD',
    query: QUERY_teamMemberPageSeniorityMethod,
    client,
    variables: {
      locationId,
    },
  };
};

export const CommunitySeniorityMethodContext = createGQLDataContext<GQLAPIContextType>({
  data: {
    fetchCommunity: {
      organization: {
        seniorityMethod: SeniorityMethodEnum.years_months_days,
      },
    },
  },
} as GQLAPIContextType<TeamMemberPageSenorityMethodQuery>);

export const CommunitySeniorityMethodProvider: FC = ({ children }) => {
  const location = getCurrentlySelectedLocation();

  const isUserAgency = useSelector(isUserAgencySelector);

  const { value: locationId } = location || { value: '' };

  const senorityMethodConfigMemo = useMemo(
    () => communitySeniorityMethodConfig(Number(locationId)),
    [locationId],
  );

  const { data, fetch, ...restQuery } =
    useGQLQuery<
      TeamMemberPageSenorityMethodQuery,
      TeamMemberPageSenorityMethodQuery,
      TeamMemberPageSenorityMethodQueryVariables
    >(senorityMethodConfigMemo);

  useEffect(() => {
    if (!restQuery?.called && locationId && !isUserAgency) fetch();
  }, [locationId]); // eslint-disable-line

  const contextValues = {
    data: {
      fetchCommunity: {
        organization: {
          seniorityMethod:
            data?.fetchCommunity?.organization?.seniorityMethod ||
            SeniorityMethodEnum.years_months_days,
        },
      },
    },
    fetch,
    ...restQuery,
  };
  return (
    <CommunitySeniorityMethodContext.Provider value={contextValues}>
      {children}
    </CommunitySeniorityMethodContext.Provider>
  );
};

export const useCommunitySeniorityMethodDataContext =
  (): GQLAPIContextType<TeamMemberPageSenorityMethodQuery> =>
    useContext(CommunitySeniorityMethodContext);
